// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/QuestionCard.tsx (L92-L108 shell: Card, header, counter)
import { useEffect, useId, useRef, type ReactNode } from 'react';

import { Card } from '@/components/ui/shadcn/card';
import { cn } from '@/lib/utils';

export interface DockedQuestionCardProps {
  /** The question's heading line. */
  title: string;
  /** One muted line under the title, inside the header. */
  subtitle?: string;
  /** A small icon before the title (decorative). */
  titleIcon?: ReactNode;
  /** The heading's id, so the answer controls can be labelled by it. Generated when not given. */
  titleId?: string;
  /** Position in the active plan; rendered as `n/N` in the header. Both or neither (a closing card has no counter). */
  index?: number;
  total?: number;
  /** Extra header content before the counter (a status such as "Still searching"). */
  headerAccessory?: ReactNode;
  /** The answer controls. A closing card ("All set") may have only a footer. */
  children?: ReactNode;
  /** The action row (Back, Skip, Next, Build my assessment). */
  footer?: ReactNode;
  /** Move keyboard focus to the heading when the card mounts, so a screen reader hears the new question. */
  focusHeading?: boolean;
  className?: string;
}

/**
 * The quiz's docked card, a shell only, on TP's QuestionCard: a flat `Card` group (`gap-3 p-4`) with a header
 * (title, optional subtitle, `n/N` counter), the answer controls and a footer row. ChatThread's `dock` slot pins
 * it to the bottom of the 760px column and plays the entrance (keyed by the step), so the card has no animation
 * of its own. Ours on top of TP: `focusHeading` and the heading's `tabIndex=-1` (TP has no focus management).
 */
export function DockedQuestionCard({
  title,
  subtitle,
  titleIcon,
  titleId,
  index,
  total,
  headerAccessory,
  children,
  footer,
  focusHeading = false,
  className,
}: DockedQuestionCardProps) {
  const generatedId = useId();
  const headingId = titleId ?? generatedId;
  const headingRef = useRef<HTMLHeadingElement>(null);
  // The card is mounted afresh for each question (callers key it), so mounting is "a new question".
  useEffect(() => {
    if (focusHeading) headingRef.current?.focus({ preventScroll: true });
  }, [focusHeading]);

  return (
    <div data-testid="docked-question">
      <Card
        role="group"
        aria-labelledby={headingId}
        data-slot="docked-question-card"
        className={cn('flex flex-col gap-3 p-4', className)}
      >
        <header className="flex items-start gap-4">
          <div className="min-w-0 flex-1">
            <h2
              ref={headingRef}
              id={headingId}
              tabIndex={-1}
              className="flex items-center gap-2 rounded-sm text-heading-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus"
            >
              {titleIcon}
              {title}
            </h2>
            {subtitle ? <p className="text-body-sm text-ds-text-2">{subtitle}</p> : null}
          </div>
          {headerAccessory}
          {index !== undefined && total !== undefined ? (
            <span
              data-testid="docked-question-counter"
              className="shrink-0 text-label text-ds-text-2"
              aria-label={`Question ${index} of ${total}`}
            >
              {index}/{total}
            </span>
          ) : null}
        </header>
        {children}
        {footer ? <footer className="flex items-center gap-2 pt-1">{footer}</footer> : null}
      </Card>
    </div>
  );
}
